import { MaskedPhoneNumberInputSchema } from '@aspendental/shared-forms-web';
import { differenceInYears, isValid, parse } from 'date-fns';
import z from 'zod';

const DATE_FORMAT = 'MM/dd/yyyy';

interface translationMappings {
	firstNameRequired: string;
	max30Chars: string;
	lastNameRequired: string;
	validEmail: string;
	dateOfBirthRequired: string;
	minYear: string;
	patientMinAge: string;
}

export const personalInfoForm = {
	firstName: {
		name: 'firstName',
		label: 'First Name',
		labelKey: 'firstName',
		schema: z
			.string()
			.trim()
			.regex(/^[a-z]+([ '-][a-z]+)*$/i, 'First Name is required')
			.max(30, 'Max of 30 characters'),
	},
	lastName: {
		name: 'lastName',
		label: 'Last Name',
		labelKey: 'lastName',
		schema: z
			.string()
			.trim()
			.regex(/^[a-z]+([ '-][a-z]+)*$/i, 'Last Name is required')
			.max(30, 'Max of 30 characters'),
	},
	email: {
		name: 'email',
		label: 'Email',
		labelKey: 'email',
		schema: z
			.string()
			.trim()
			.regex(/\@/, 'Please enter valid email address')
			.min(5, 'Please enter valid email address'),
	},
	phone: {
		name: 'phone',
		label: 'Phone',
		labelKey: 'phone',
		schema: MaskedPhoneNumberInputSchema.options.default,
	},
	dateOfBirth: {
		name: 'dateOfBirth',
		label: 'Date of Birth',
		labelKey: 'dateOfBirth',
		schema: z
			.string()
			.refine(
				(val) => {
					const parsedDate = parse(val, DATE_FORMAT, new Date());
					return isValid(parsedDate);
				},
				{
					message: 'Valid Date of Birth is required',
				}
			)
			.refine(
				(val) => {
					const [_month, _day, year] = val.split('/').map(Number);
					return year > 1900;
				},
				{
					message: 'Year must be greater than 1900',
				}
			)
			.transform((val) => {
				const [month, day, year] = val.split('/').map(Number);
				return new Date(year, month - 1, day);
			})
			.refine(
				(val) => {
					const yearsSinceDate = differenceInYears(new Date(), val);
					return yearsSinceDate >= 18;
				},
				{
					message: 'Patient must be 18 or over',
				}
			),
	},
};

export const personalInfoFormSchema = [
	{ name: personalInfoForm.firstName.name, validation: personalInfoForm.firstName.schema },
	{ name: personalInfoForm.lastName.name, validation: personalInfoForm.lastName.schema },
	{ name: personalInfoForm.email.name, validation: personalInfoForm.email.schema },
	{ name: personalInfoForm.phone.name, validation: personalInfoForm.phone.schema },
	{ name: personalInfoForm.dateOfBirth.name, validation: personalInfoForm.dateOfBirth.schema },
];

export default function generatePersonalInfoFormSchema(translations: translationMappings) {
	const personalInfoFormConfiguration = {
		firstName: {
			name: 'firstName',
			label: 'First Name',
			labelKey: 'firstName',
			schema: z
				.string()
				.trim()
				.regex(/^[a-z]+([ '-][a-z]+)*$/i, translations.firstNameRequired)
				.max(30, translations.max30Chars),
		},
		lastName: {
			name: 'lastName',
			label: 'Last Name',
			labelKey: 'lastName',
			schema: z
				.string()
				.trim()
				.regex(/^[a-z]+([ '-][a-z]+)*$/i, translations.lastNameRequired)
				.max(30, translations.max30Chars),
		},
		email: {
			name: 'email',
			label: 'Email',
			labelKey: 'email',
			schema: z.string().trim().regex(/\@/, translations.validEmail).min(5, translations.validEmail),
		},
		phone: {
			name: 'phone',
			label: 'Phone',
			labelKey: 'phone',
			// TODO: look into configuring translation for this phone num
			schema: MaskedPhoneNumberInputSchema.options.default,
		},
		dateOfBirth: {
			name: 'dateOfBirth',
			label: 'Date of Birth',
			labelKey: 'dateOfBirth',
			schema: z
				.string()
				.refine(
					(val) => {
						const parsedDate = parse(val, DATE_FORMAT, new Date());
						return isValid(parsedDate);
					},
					{
						message: translations.dateOfBirthRequired,
					}
				)
				.refine(
					(val) => {
						const [_month, _day, year] = val.split('/').map(Number);
						return year > 1900;
					},
					{
						message: translations.minYear,
					}
				)
				.transform((val) => {
					const [month, day, year] = val.split('/').map(Number);
					return new Date(year, month - 1, day);
				})
				.refine(
					(val) => {
						const yearsSinceDate = differenceInYears(new Date(), val);
						return yearsSinceDate >= 18;
					},
					{
						message: translations.patientMinAge,
					}
				),
		},
	};

	return [
		{
			name: personalInfoFormConfiguration.firstName.name,
			validation: personalInfoFormConfiguration.firstName.schema,
		},
		{
			name: personalInfoFormConfiguration.lastName.name,
			validation: personalInfoFormConfiguration.lastName.schema,
		},
		{ name: personalInfoFormConfiguration.email.name, validation: personalInfoFormConfiguration.email.schema },
		{ name: personalInfoFormConfiguration.phone.name, validation: personalInfoFormConfiguration.phone.schema },
		{
			name: personalInfoFormConfiguration.dateOfBirth.name,
			validation: personalInfoFormConfiguration.dateOfBirth.schema,
		},
	];
}
